import { IconButton, Tooltip } from "@bonusx-global/bx-design-system";
import { ChatsDotsOutlined } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import Badge from "@mui/material/Badge";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ZERO_DIGIT } from "../../../../../../constants/env";
import useIsMobile from "../../../../../../hooks/device/useIsMobile";
import { useChatMessageStore } from "../../../../../hooks/use-chat-message-store/use-chat-message-store";
import { useChatMessageWindowStore } from "../../../../../hooks/useChatMessageWindowStore";
import { useChatWithOperatorState } from "../../../../../hooks/useChatWithOperatorState";
import { useLegacyServiceRequestApi } from "../../../../../hooks/useLegacyServiceRequestApi";
import type { IdDetailParams } from "../../../../../interfaces/IdDetailParams.interface";
import { MessageSender, tryGetBelongingUnreadMessages } from "../../../../../utils/tryGetBelongingUnreadMessages";
import { useChatWithOperatorTooltip } from "./hooks/use-chat-with-operator-tooltip.hook";

export const chatWithOperatorIconButtonId = "chat-with-operator-icon-button";
export const chatWithOperatorBadgeId = "chat-with-operator-icon-badge";

const ChatsDotsOutlinedWithNoPointerEvent = styled(ChatsDotsOutlined)({
  pointerEvents: "none",
});

function HeaderV2ChatWithOperatorIcon(): JSX.Element {
  const isMobile = useIsMobile();
  const { id: practiceId } = useParams<IdDetailParams>();
  const parsedPracticeId = practiceId ? parseInt(practiceId) : 0;
  const { serviceRequest } = useLegacyServiceRequestApi(practiceId ? parseInt(practiceId) : 0);

  const { toggleChat } = useChatMessageWindowStore();
  const { isChatEnabled, isPracticeSentToOperator } = useChatWithOperatorState();

  const { tooltipDescription, isTooltipDisabled } = useChatWithOperatorTooltip();

  const { messages } = useChatMessageStore(parsedPracticeId);

  const messagesWithOperatorCount = useMemo(
    function memoizeMessagesWithOperatorCount() {
      if (!isChatEnabled) return ZERO_DIGIT;

      const unreadMessages = tryGetBelongingUnreadMessages(MessageSender.Citizen, serviceRequest, messages);

      return unreadMessages.length;
    },
    [messages, isChatEnabled, serviceRequest]
  );

  const openChatCallback = useCallback(() => {
    if (isChatEnabled) {
      toggleChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatEnabled]);

  const disablePointerEvents = { pointerEvents: "none" };

  return (
    <Tooltip
      placement={isMobile ? "bottom" : "left"}
      description={tooltipDescription}
      disableHoverListener={isTooltipDisabled}
      enterTouchDelay={0}
    >
      <div>
        <IconButton
          color="primary"
          onClick={openChatCallback}
          disabled={!isChatEnabled || !isPracticeSentToOperator}
          id={chatWithOperatorIconButtonId}
        >
          <Badge
            sx={disablePointerEvents}
            color="error"
            badgeContent={messagesWithOperatorCount}
            id={chatWithOperatorBadgeId}
          >
            <ChatsDotsOutlinedWithNoPointerEvent />
          </Badge>
        </IconButton>
      </div>
    </Tooltip>
  );
}

export default HeaderV2ChatWithOperatorIcon;
